import { useCallback, useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { useTranslation } from "react-i18next";

import { CircularProgress } from "@mui/material";

import { DndProvider } from "react-dnd";

import { HTML5Backend } from "react-dnd-html5-backend";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";
import {
	CreateQuestionnaireStep,
	CreateQuestionnaireStepSchema,
	QuestionnaireStep,
} from "@/pages/Public/pages/Questionnaire/schema/questionnaire";

import {
	useCreateQuestionnaireStepMutation,
	useDeleteQuestionnaireStepMutation,
	useUpdateQuestionnaireStepNumberMutation,
} from "@/pages/Public/redux/questionnaire/questionnaire.api";

import DeleteModal from "@/components/Modals/DeleteModal";

import EditableQuestionnaireItem from "./EditableQuestionnaireItem";
import EditModeQuestionnaireItem from "./EditableQuestionnaireItem/EditModeQuestionnaireItem";

interface EditableQuestionnaireListProps {
	data?: QuestionnaireStep[];
	isLoading: boolean;
	editingQuestionnaireId: number;
	setEditingQuestionnaireId: (value: number) => void;
}

export default function EditableQuestionnaireList({
	data,
	isLoading,
	editingQuestionnaireId,
	setEditingQuestionnaireId,
}: EditableQuestionnaireListProps) {
	const { t } = useTranslation();
	const ts = (key: string, options = {}) => t(`manage-questionnaire.${key}`, options);
	const [isCreating, setCreating] = useState(0);
	const [changeStepNumber, { isLoading: isChangingStepNumber }] =
		useUpdateQuestionnaireStepNumberMutation();

	const [deletingQuestionnaire, setDeletingQuestionnaire] = useState<QuestionnaireStep | null>(
		null
	);
	const [createQuestionnaire, { isLoading: isCreatingQuestionnaire }] =
		useCreateQuestionnaireStepMutation();
	const [deleteQuestionnaireStep, { isLoading: isQuestionnaireDeleting }] =
		useDeleteQuestionnaireStepMutation();

	const dropQuestion = useCallback(
		(dragElementId: number, dragStepNumber: number, dropStepNumber: number) => {
			if (dragStepNumber !== dropStepNumber) {
				handleChangeStepNumber(dragElementId, dropStepNumber);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const handleChangeStepNumber = async (itemId: number, newStepNumber: number) => {
		try {
			await changeStepNumber({ id: itemId, stepNumber: newStepNumber });
		} catch (error) {
			console.error(error);
		}
	};

	const handleCreateQuestionnaire = async (values: CreateQuestionnaireStep) => {
		try {
			await createQuestionnaire(values);
		} catch (error) {
			console.error(error);
		} finally {
			setCreating(0);
		}
	};

	const handleDeleteQuestionnaire = async () => {
		if (!deletingQuestionnaire?.id) {
			return;
		}

		try {
			await deleteQuestionnaireStep(deletingQuestionnaire?.id ?? 0).unwrap();
			setDeletingQuestionnaire(null);
		} catch (err) {
			console.error(err);
		}
	};

	const onEditQuestionnaire = (id: number) => {
		setEditingQuestionnaireId(id);
		setCreating(0);
	};

	return (
		<DndProvider backend={HTML5Backend}>
			<div
				className={`w-full border border-primary-gray-lightest p-1 pt-4 rounded-16 space-y-4 bg-[#F7F8FB]`}
			>
				<div className="flex justify-between items-center px-3">
					<h3 className="w-full flex gap-2 text-primary-blue-dark text-[14px] font-medium">
						{ts("questions")}

						<span className="border border-primary-gray-lightest bg-primary-gray-lightest rounded-[4px] w-5 h-5 flex items-center justify-center text-[#777D8D] text-[12px] font-medium">
							{data?.length ?? "0"}
						</span>
					</h3>
					<Button
						className="!min-w-5 !min-h-5 !max-w-5 !max-h-5 !p-0"
						color={ButtonColor.ACTION}
						image={<Icon className="w-3 h-4 text-white" icon={faPlus} />}
						title=""
						type="button"
						onClick={() => {
							setCreating(1);
							if (editingQuestionnaireId !== -1) {
								setEditingQuestionnaireId(-1);
							}
						}}
					/>
				</div>
				{isLoading ? (
					<div className="w-full flex justify-center py-10">
						<CircularProgress className="!w-12 !h-12" />
					</div>
				) : (
					<div className="space-y-1 relative">
						{(isChangingStepNumber || isQuestionnaireDeleting) && (
							<div className="w-full h-full absolute flex justify-center py-12 bg-white opacity-50">
								<CircularProgress className="!w-12 !h-12" />
							</div>
						)}
						{isCreating === 1 && (
							<EditModeQuestionnaireItem
								data={{ stepNumber: (data?.length ?? 0) + 1 }}
								isLoading={isCreatingQuestionnaire}
								setEditing={setEditingQuestionnaireId}
								validationSchema={CreateQuestionnaireStepSchema}
								onSubmit={handleCreateQuestionnaire}
							/>
						)}
						{data?.length ? (
							data?.map((item) => (
								<EditableQuestionnaireItem
									key={item?.id}
									data={item}
									dropQuestion={dropQuestion}
									isEditing={editingQuestionnaireId === item?.id}
									isEditingInProcess={isCreating !== 0 || editingQuestionnaireId !== -1}
									setDeletingQuestionnaire={setDeletingQuestionnaire}
									setEditing={onEditQuestionnaire}
								/>
							))
						) : (
							<p className="text-[16px] text-[#777D8D] text-center py-4">{ts("list-empty")}</p>
						)}
						{isCreating === -1 && (
							<EditModeQuestionnaireItem
								data={{ stepNumber: (data?.length ?? 0) + 1 }}
								isLoading={isCreatingQuestionnaire}
								setEditing={setEditingQuestionnaireId}
								validationSchema={CreateQuestionnaireStepSchema}
								onSubmit={handleCreateQuestionnaire}
							/>
						)}

						<Button
							className="!text-[14px] !m-3 !mt-4 !w-fit !font-medium leading-6"
							image={<Icon className="text-white w-5 h-5 mr-2" icon={faPlus} />}
							title={ts("addQuestion")}
							type="button"
							onClick={() => {
								setCreating(-1);
								if (editingQuestionnaireId !== -1) {
									setEditingQuestionnaireId(-1);
								}
							}}
						/>
					</div>
				)}
			</div>
			<DeleteModal
				description={ts("delete-description", { taskTitle: deletingQuestionnaire?.title })}
				handleClose={() => setDeletingQuestionnaire(null)}
				handleDelete={handleDeleteQuestionnaire}
				isLoading={isQuestionnaireDeleting}
				isShowing={!!deletingQuestionnaire}
				title={ts("delete-title")}
			/>
		</DndProvider>
	);
}
